/**
 * Cabinet geometry builders — ported from viewer_3d.py buildCabinetBox, buildBackPanel,
 * buildStretchers, buildNailers, buildShelves, buildFaceComponents.
 *
 * All dimensions in inches. Uses center-based positioning (Three.js default).
 * Returns arrays of PanelDescriptor that CabinetModel maps to <mesh> elements.
 */

import type {
  CabinetData,
  PanelDescriptor,
  Adjacency,
  FaceComponentConfig,
} from './types';

// ── Constants (matching Python viewer) ────────────────────────────────────────
const PT = 0.75;     // panel thickness
const BT = 0.25;     // back panel thickness

// ── Box panels ────────────────────────────────────────────────────────────────
export function buildBoxPanels(cab: CabinetData): PanelDescriptor[] {
  const { width: W, height: H, depth: D, config: cfg } = cab;
  const panels: PanelDescriptor[] = [];

  const tkH = cfg.toe_kick_style === 'integrated' ? cfg.toe_kick_height : 0;

  // Left panel
  if (cfg.has_left_panel) {
    panels.push({
      name: 'Left Panel',
      width: PT, height: H, depth: D,
      x: PT / 2, y: H / 2, z: D / 2,
      colorType: 'box',
    });
  }

  // Right panel
  if (cfg.has_right_panel) {
    panels.push({
      name: 'Right Panel',
      width: PT, height: H, depth: D,
      x: W - PT / 2, y: H / 2, z: D / 2,
      colorType: 'box',
    });
  }

  // Bottom panel
  if (cfg.has_bottom_panel) {
    const iw = W - 2 * PT;
    const bottomY = tkH > 0 ? tkH + PT / 2 : PT / 2;
    panels.push({
      name: 'Bottom Panel',
      width: iw, height: PT, depth: D,
      x: W / 2, y: bottomY, z: D / 2,
      colorType: 'box',
    });
  }

  // Top panel (only if full_panel style)
  if (cfg.top_style === 'full_panel') {
    const iw = W - 2 * PT;
    panels.push({
      name: 'Top Panel',
      width: iw, height: PT, depth: D,
      x: W / 2, y: H - PT / 2, z: D / 2,
      colorType: 'box',
    });
  }

  return panels;
}

// ── Back panel ────────────────────────────────────────────────────────────────
export function buildBackPanel(cab: CabinetData): PanelDescriptor[] {
  const { width: W, height: H, depth: _D, config: cfg } = cab;
  if (!cfg.has_back_panel) return [];

  const tkH = cfg.toe_kick_style === 'integrated' ? cfg.toe_kick_height : 0;
  const iw = W - 2 * PT;
  const bottomRef = tkH + PT;
  const ih = H - PT - tkH;

  return [{
    name: 'Back Panel',
    width: iw, height: ih, depth: BT,
    x: W / 2, y: bottomRef + ih / 2, z: PT + BT / 2,
    colorType: 'back',
  }];
}

// ── Stretchers ────────────────────────────────────────────────────────────────
export function buildStretchers(cab: CabinetData): PanelDescriptor[] {
  const { width: W, height: H, depth: D, config: cfg } = cab;
  if (cfg.top_style === 'full_panel') return [];

  const panels: PanelDescriptor[] = [];
  const iw = W - 2 * PT;
  const sw = cfg.top_stretcher_width;
  const qty = cfg.top_stretcher_qty;

  // Front stretcher
  if (qty >= 1) {
    panels.push({
      name: 'Front Stretcher',
      width: iw, height: PT, depth: sw,
      x: W / 2, y: H - PT / 2, z: D - sw / 2,
      colorType: 'stretcher',
    });
  }

  // Back stretcher
  if (qty >= 2) {
    panels.push({
      name: 'Back Stretcher',
      width: iw, height: PT, depth: sw,
      x: W / 2, y: H - PT / 2, z: PT + BT + sw / 2,
      colorType: 'stretcher',
    });
  }

  // Middle stretcher
  if (qty >= 3) {
    panels.push({
      name: 'Middle Stretcher',
      width: iw, height: PT, depth: sw,
      x: W / 2, y: H - PT / 2, z: D / 2,
      colorType: 'stretcher',
    });
  }

  return panels;
}

// ── Nailers ───────────────────────────────────────────────────────────────────
export function buildNailers(cab: CabinetData): PanelDescriptor[] {
  const { width: W, height: H, config: cfg } = cab;
  const panels: PanelDescriptor[] = [];
  const iw = W - 2 * PT;
  const nw = cfg.back_nailer_width;
  const qty = cfg.back_nailer_qty;
  const tkH = cfg.toe_kick_style === 'integrated' ? cfg.toe_kick_height : 0;
  const bottomRef = tkH + PT;

  // Top nailer
  if (qty >= 1) {
    panels.push({
      name: 'Top Nailer',
      width: iw, height: nw, depth: PT,
      x: W / 2, y: H - nw / 2, z: PT / 2,
      colorType: 'nailer',
    });
  }

  // Bottom nailer
  if (qty >= 2) {
    panels.push({
      name: 'Bottom Nailer',
      width: iw, height: nw, depth: PT,
      x: W / 2, y: bottomRef + nw / 2, z: PT / 2,
      colorType: 'nailer',
    });
  }

  return panels;
}

// ── Shelves ───────────────────────────────────────────────────────────────────
export function buildShelves(cab: CabinetData): PanelDescriptor[] {
  const { width: W, height: H, depth: D, num_shelves, config: cfg } = cab;
  if (num_shelves <= 0) return [];

  const panels: PanelDescriptor[] = [];
  const tkH = cfg.toe_kick_style === 'integrated' ? cfg.toe_kick_height : 0;
  const sw = W - 2 * PT;
  const sd = D - PT - BT;
  const innerBot = tkH + PT;
  const innerTop = H - PT;
  const available = innerTop - innerBot;
  const spacing = available / (num_shelves + 1);

  for (let i = 1; i <= num_shelves; i++) {
    panels.push({
      name: `Shelf ${i}`,
      width: sw, height: PT, depth: sd,
      x: W / 2, y: innerBot + spacing * i, z: PT + BT + sd / 2,
      colorType: 'shelf',
    });
  }

  return panels;
}

// ── Face components (doors / drawers) ─────────────────────────────────────────
export function buildFaceComponents(
  cab: CabinetData,
  adjacency: Adjacency = { left: false, right: false },
): PanelDescriptor[] {
  const { width: W, height: H, depth: D, config: cfg } = cab;
  const panels: PanelDescriptor[] = [];

  const tkH = cfg.toe_kick_style !== 'none' ? cfg.toe_kick_height : 0;
  const ovTop = cfg.door_overlay_top;
  const ovBottom = cfg.door_overlay_bottom;
  const ovLeft = cfg.door_overlay_left;
  const ovRight = cfg.door_overlay_right;
  const gap = cfg.door_gap;
  const reveal = cfg.door_reveal;

  const faceBottom = tkH - ovBottom;
  const faceTop = H + ovTop;
  const faceHeight = faceTop - faceBottom;

  const revealLeft = adjacency.left ? reveal / 2 : 0;
  const revealRight = adjacency.right ? reveal / 2 : 0;
  const faceLeft = -ovLeft + revealLeft;
  const faceRight = W + ovRight - revealRight;
  const faceWidth = faceRight - faceLeft;
  const faceCenterX = (faceLeft + faceRight) / 2;

  const dz = D + PT / 2; // doors project forward from cabinet

  // Parse face_component_config
  let fcc: FaceComponentConfig | null = null;
  try {
    const parsed = JSON.parse(cab.face_component_config || '{}');
    if (parsed.components && parsed.components.length > 0) {
      fcc = parsed as FaceComponentConfig;
    }
  } catch { /* fallback below */ }

  if (fcc && fcc.components.length > 0) {
    // New mode: explicit component config
    const comps = fcc.components;
    const numComps = comps.length;
    const totalGap = gap * Math.max(0, numComps - 1);
    const availHeight = faceHeight - totalGap;

    // Compute heights
    const heights: number[] = [];
    if (fcc.evenly_distribute) {
      const h = availHeight / numComps;
      for (let i = 0; i < numComps; i++) heights.push(h);
    } else {
      let used = 0;
      for (let i = 0; i < numComps; i++) {
        if (i === numComps - 1) {
          heights.push(availHeight - used);
        } else {
          const ch = comps[i].height ?? availHeight / numComps;
          heights.push(ch);
          used += ch;
        }
      }
    }

    // Build bottom to top (array reversed)
    let currentY = faceBottom;
    let doorCount = 0;
    let drawerCount = 0;

    for (let i = comps.length - 1; i >= 0; i--) {
      const comp = comps[i];
      const compH = heights[i];
      const dy = currentY + compH / 2;

      switch (comp.type) {
        case '1door':
          doorCount++;
          panels.push({
            name: `Door ${doorCount}`,
            width: faceWidth, height: compH, depth: PT,
            x: faceCenterX, y: dy, z: dz,
            colorType: 'door',
          });
          break;

        case '2door': {
          const dw = (faceWidth - gap) / 2;
          for (let j = 0; j < 2; j++) {
            doorCount++;
            panels.push({
              name: `Door ${doorCount}`,
              width: dw, height: compH, depth: PT,
              x: faceLeft + dw / 2 + j * (dw + gap), y: dy, z: dz,
              colorType: 'door',
            });
          }
          break;
        }

        case '1drawer':
          drawerCount++;
          panels.push({
            name: `Drawer ${drawerCount}`,
            width: faceWidth, height: compH, depth: PT,
            x: faceCenterX, y: dy, z: dz,
            colorType: 'drawer',
          });
          break;

        case '2drawer': {
          const dw = (faceWidth - gap) / 2;
          for (let j = 0; j < 2; j++) {
            drawerCount++;
            panels.push({
              name: `Drawer ${drawerCount}`,
              width: dw, height: compH, depth: PT,
              x: faceLeft + dw / 2 + j * (dw + gap), y: dy, z: dz,
              colorType: 'drawer',
            });
          }
          break;
        }
      }

      currentY += compH + (i > 0 ? gap : 0);
    }
  } else {
    // Fallback: use num_doors / num_drawers fields
    const totalComps = cab.num_doors + cab.num_drawers;
    if (totalComps <= 0) return panels;

    const totalGap = gap * Math.max(0, totalComps - 1);
    const availHeight = faceHeight - totalGap;
    const compH = availHeight / totalComps;
    let currentY = faceBottom;

    // Drawers at bottom
    for (let i = 0; i < cab.num_drawers; i++) {
      panels.push({
        name: `Drawer ${i + 1}`,
        width: faceWidth, height: compH, depth: PT,
        x: faceCenterX, y: currentY + compH / 2, z: dz,
        colorType: 'drawer',
      });
      currentY += compH + gap;
    }

    // Doors on top
    for (let i = 0; i < cab.num_doors; i++) {
      panels.push({
        name: `Door ${i + 1}`,
        width: faceWidth, height: compH, depth: PT,
        x: faceCenterX, y: currentY + compH / 2, z: dz,
        colorType: 'door',
      });
      currentY += compH + gap;
    }
  }

  return panels;
}

// ── Integrated toe kick notch visualization ───────────────────────────────────
export function buildToeKickNotch(cab: CabinetData): PanelDescriptor[] {
  // For V1 we skip CSG operations and just show a darker panel at the toe kick area
  const { width: W, config: cfg } = cab;
  if (cfg.toe_kick_style !== 'integrated') return [];

  const tkH = cfg.toe_kick_height;
  const tkDepth = 3.0; // standard 3" toe kick depth
  const iw = W - 2 * PT;

  return [{
    name: 'Toe Kick Recessed Panel',
    width: iw, height: tkH, depth: 0.1,
    x: W / 2, y: tkH / 2, z: W - tkDepth,
    colorType: 'back', // dark color
  }];
}

// ── Assemble all panels for a cabinet ─────────────────────────────────────────
export function buildAllPanels(
  cab: CabinetData,
  adjacency: Adjacency = { left: false, right: false },
): PanelDescriptor[] {
  return [
    ...buildBoxPanels(cab),
    ...buildBackPanel(cab),
    ...buildStretchers(cab),
    ...buildNailers(cab),
    ...buildShelves(cab),
    ...buildFaceComponents(cab, adjacency),
    ...buildToeKickNotch(cab),
  ];
}
